import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { maxBy, minBy } from '@sama/micro-dash';
import { ShapeLocationState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  getShapeForEventTracking,
  ShapeState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { SubpointEventInfo } from '../../../../cross-team-shared/misc-services/events/mouse-event.service';
import { EventTrackingValue } from '../../../../team-apis/core/event-tracking';
import { ClickxOptions } from '../../../../team-apis/raccoons/clickx-options';
import { ensureKeyLocation, LocationHelper } from '../../utils/location-helper';
import { SingleSubpointTranslationHelper } from '../edit/single-subpoint-translation-helper';
import { VectorDragHandler } from '../shared/drag-handlers/vector-drag-handler';
import { getClickxOptionsOrThrow } from './clickx-tool-helper';
import { PolygonInferenceService } from './polygon-inference.service';

export class PolygonInferenceDragHandler extends VectorDragHandler {
  protected override shouldTrackBatchEvents = false;

  private clickxOptions: ClickxOptions;
  private polygonInferenceService: PolygonInferenceService;
  private shapeBeforeDrag: ShapeState2D;
  private clickedShapeStore: Store<ShapeState2D>;
  private pointIndex: number;

  constructor(
    eventInfo: SubpointEventInfo,
    injector: Injector,
    translationHelper: SingleSubpointTranslationHelper,
  ) {
    super(eventInfo.event, injector, translationHelper);
    this.polygonInferenceService = injector.get(PolygonInferenceService);
    this.shapeBeforeDrag = eventInfo.shapeStore.state();
    this.clickxOptions = getClickxOptionsOrThrow(this.store.state().output);
    this.clickedShapeStore = eventInfo.shapeStore;
    this.pointIndex = eventInfo.subpointIndex;
  }

  override cancel(eventTrackingValue: EventTrackingValue) {
    this.clickedShapeStore.set(this.shapeBeforeDrag);
    this.shellConnector.trackEvent({
      id: 'cancel_shape_edit',
      bucket: 'annotation',
      ...eventTrackingValue,
      shapes: [getShapeForEventTracking(this.clickedShapeStore.state())],
    });
    super.cancel(eventTrackingValue);
  }

  protected override async performAsyncStep() {
    const release = this.shellConnector.restrictInteractions('none');
    void this.completion.finally(release);

    const locationStore = ensureKeyLocation(
      new LocationHelper(this.store),
      this.store('shapes')(this.shapeBeforeDrag.index)('key_locations'),
      this.store.state().frameNumber,
    );

    this.addInputPointToLocation(locationStore);
    await this.polygonInferenceService.infer(
      locationStore,
      this,
      this.clickedShapeStore.state().index,
    );
  }

  protected override complete() {
    this.clickedShapeStore('source').set({
      model_id: this.clickxOptions.model_id,
      name: 'edit_tool_clicky',
    });
    this.translationHelper.pushAndTrackEvent({
      ...this.batchValues?.buildEventTrackingValue(),
      source: 'edit_tool',
    });
  }

  private addInputPointToLocation(locationStore: Store<ShapeLocationState2D>) {
    const location = locationStore.state();
    locationStore('model_input_points').setUsing((modelInputPoints) => {
      if (!modelInputPoints) {
        // fix me if you change this code
        // eslint-disable-next-line no-param-reassign -- fix me!
        modelInputPoints = [
          minBy(location.points, (p) => p[0]),
          minBy(location.points, (p) => p[1]),
          maxBy(location.points, (p) => p[0]),
          maxBy(location.points, (p) => p[1]),
        ];
      }
      return [...modelInputPoints, location.points[this.pointIndex]];
    });
  }
}
